import { useEffect, useMemo, useState } from "react";
import DashboardParams from "../interfaces/dashboardParams";
import useDashboardStore from "../store/dashboardStore";
import "./DashboardFilter.css";
import useListMedal from "../hooks/useListMedal";

const toInputDate = (value: string | null) => {
  if (!value) return null;
  return value.split("T")[0] || null;
};

const toStartDateTime = (value: string | null) =>
  value ? `${value}T00:00:00` : null;

const toEndDateTime = (value: string | null) =>
  value ? `${value}T23:59:59` : null;

const DashboardFilter = () => {
  const params = useDashboardStore((state) => state.params);
  const setParams = useDashboardStore((state) => state.setParams);
  const [draftParams, setDraftParams] = useState<DashboardParams>({
    ...params,
    startDate: toInputDate(params.startDate),
    endDate: toInputDate(params.endDate),
  });

  const { data: medalList, isLoading, error } = useListMedal();

  useEffect(() => {
    setDraftParams({
      ...params,
      startDate: toInputDate(params.startDate),
      endDate: toInputDate(params.endDate),
    });
  }, [params]);

  const selectedMedals = draftParams.medals ?? [];

  const hasPendingChanges = useMemo(() => {
    const normalizeMedals = (medals: string[] | null) =>
      [...(medals ?? [])].sort().join("|");

    const appliedStartDate = toInputDate(params.startDate);
    const appliedEndDate = toInputDate(params.endDate);

    return (
      normalizeMedals(draftParams.medals) !== normalizeMedals(params.medals) ||
      draftParams.startDate !== appliedStartDate ||
      draftParams.endDate !== appliedEndDate
    );
  }, [draftParams, params]);

  const toggleMedal = (medalName: string) => {
    const isSelected = selectedMedals.includes(medalName);
    const values = isSelected
      ? selectedMedals.filter((name) => name !== medalName)
      : [...selectedMedals, medalName];

    setDraftParams({
      ...draftParams,
      medals: values.length > 0 ? values : null,
    });
  };

  const handleDateChange = (field: "startDate" | "endDate", value: string) => {
    setDraftParams({
      ...draftParams,
      [field]: value || null,
    });
  };

  const clearDraftFilters = () => {
    setDraftParams({
      medals: null,
      startDate: null,
      endDate: null,
    });
  };

  const applyFilters = () => {
    setParams({
      ...draftParams,
      startDate: toStartDateTime(draftParams.startDate),
      endDate: toEndDateTime(draftParams.endDate),
    });
  };

  const hasActiveFilters =
    selectedMedals.length > 0 ||
    Boolean(draftParams.startDate) ||
    Boolean(draftParams.endDate);

  if (isLoading)
    return <div className="dashboard-filter-card">Loading filters...</div>;
  if (error)
    return (
      <div className="dashboard-filter-card">
        Error loading filters: {error.message}
      </div>
    );

  return (
    <section className="dashboard-filter-card" aria-label="Dashboard filters">
      <div className="dashboard-filter-header">
        <h2>Dashboard Filter</h2>
        <div className="dashboard-filter-actions">
          <button
            type="button"
            className="dashboard-filter-clear"
            onClick={clearDraftFilters}
            disabled={!hasActiveFilters}
          >
            Clear draft
          </button>
          <button
            type="button"
            className="dashboard-filter-apply"
            onClick={applyFilters}
            disabled={!hasPendingChanges}
          >
            Apply filters
          </button>
        </div>
      </div>

      <div className="dashboard-filter-dates">
        <label className="dashboard-filter-field">
          <span>Start date</span>
          <input
            type="date"
            value={draftParams.startDate ?? ""}
            onChange={(e) => handleDateChange("startDate", e.target.value)}
            max={draftParams.endDate ?? undefined}
          />
        </label>

        <label className="dashboard-filter-field">
          <span>End date</span>
          <input
            type="date"
            value={draftParams.endDate ?? ""}
            onChange={(e) => handleDateChange("endDate", e.target.value)}
            min={draftParams.startDate ?? undefined}
          />
        </label>
      </div>

      <label className="dashboard-filter-field dashboard-filter-medals-field">
        <span>Medals</span>
        <details className="dashboard-medals-dropdown">
          <summary>
            {selectedMedals.length === 0
              ? "All medals"
              : `${selectedMedals.length} medal(s) selected`}
          </summary>

          <div
            className="dashboard-medals-menu"
            role="listbox"
            aria-multiselectable="true"
          >
            {medalList?.map((medal) => {
              const isSelected = selectedMedals.includes(medal.name);
              return (
                <button
                  type="button"
                  key={medal.name}
                  className={`dashboard-medals-option ${isSelected ? "selected" : ""}`}
                  onClick={() => toggleMedal(medal.name)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="dashboard-medals-check" aria-hidden="true">
                    {isSelected ? "x" : ""}
                  </span>
                  <span>{medal.name}</span>
                </button>
              );
            })}

            <button
              type="button"
              className="dashboard-medals-clear-selection"
              onClick={() =>
                setDraftParams({
                  ...draftParams,
                  medals: null,
                })
              }
            >
              Clear medal selection
            </button>
          </div>
        </details>
        <p className="dashboard-filter-hint">
          Adjust filters, then click Apply filters to refresh dashboard data.
        </p>
        {medalList?.length === 0 && (
          <p className="dashboard-filter-empty">
            No medals available for filtering yet.
          </p>
        )}
      </label>
    </section>
  );
};

export default DashboardFilter;
